import { defineStore } from "pinia";
import joi from "joi";
import md5 from "md5";
import type { IDefaultState, IFileState } from "@/interfaces";
import type { StoreName, TabErrorSchemaTypes } from "@/types";
import helpers from "@/helpers";
import { globali18n } from "@/i18n";
import toImageList from "@/helpers/files/toImageList";
import { type MealDishInput } from "@/graphql/generated-types";
import type { MealDishFragment } from "@/graphql";
import { useMealDishes } from "@/composables/useMealDishes";

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title"],
  pictures: ["imageList"],
};

export type MealDishItem = {
  id?: number;
  title: string;
  description: string;
  imageList: IFileState[];
};

export interface IMealDishesState extends IDefaultState {
  item: MealDishItem;
  areCategoriesFetched: boolean;
  mealDishes: ReturnType<typeof useMealDishes>;
}

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IMealDishesState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      areCategoriesFetched: false,
      mealDishes: useMealDishes(true),
      item: {
        title: "",
        description: "",
        imageList: [],
      },
    }),
    actions: {
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      getValidationSchema(items: MealDishFragment[], id: number = 0) {
        return joi.object({
          title: joi
            .string()
            .max(50)
            .required()
            .custom((value, helper) => {
              const exists = items.some(
                (item) =>
                  item?.title.toLowerCase() === value.toLowerCase() &&
                  id.toString() !== item.id,
              );

              if (!exists) {
                return true;
              }

              const { t } = globali18n;
              return helper.error(t("global.error_category_exists"));
            }),
          imageList: joi.array().min(1).required(),
          description: joi.string().max(2000).optional(),
        });
      },
      async setItem(data: MealDishFragment) {
        this.item = {
          id: data.id ? parseInt(data.id) : undefined,
          title: data.title,
          description: data.description || "",
          imageList: toImageList(data),
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem() {
        const item = { ...this.item };

        return {
          id: item?.id?.toString(),
          title: item.title,
          description: item.description,
        };
      },
      async isItemNotValid(mealDishes: MealDishFragment[], id: number = 0) {
        const schema = this.getValidationSchema(mealDishes, id);

        this.validation = helpers.forms.validate(schema, {
          title: this.item.title,
          imageList: this.item.imageList.filter(
            (item) => item.action !== "delete",
          ),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async save(input: MealDishInput) {
        return await this.mealDishes.save(input);
      },
      async deleteItem() {
        if (!this.item.id) {
          return;
        }

        return await this.mealDishes.deleteItem(this.item.id.toString());
      },
    },
  });
};

export const useMealDishesStore = createUseStore("MEALS_DISHES");
export const useAddMealDishesStore = createUseStore("MEALS_ADD_DISHES");

export type MealDishesStore = ReturnType<typeof useMealDishesStore>;
